import { Modal } from '../ui/Modal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, User, Car, Calendar as CalendarIcon, Check, ChevronRight, UserPlus } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { supabase } from '../../lib/supabase';
import { VRMLookup, type FinalVehicleData, type LinkedCustomerData } from '../vehicles/VRMLookup';
import { APPOINTMENT_TYPES } from './appointmentTypes';
import { reportError } from '../../lib/errors';
import { motOdometerOffer } from '../../lib/vehicle';
import { MotMileageOffer } from '../vehicles/MotMileageOffer';

/**
 * Booking Modal — one screen, top to bottom.
 *
 *   Vehicle (reg lookup) → Who → What for? → When → Confirm
 *
 * The vehicle comes FIRST and its fields are always visible. It used to be a
 * gated two-step wizard where the lookup collapsed into a chip reading
 * "AY16NHN · PEUGEOT 208", and everything else DVLA had returned — year,
 * colour, engine, fuel type, MOT and tax expiry — was shown once and then
 * dropped, because `fn_create_appointment_with_details` only took vrm/make/
 * model. Two screens later the quote builder read the vehicle row back and had
 * blank Year / Colour / Mileage / Engine / Fuel Type, so the mechanic retyped
 * details the app had already fetched. Migration 20260820120000 widened the RPC;
 * this component fills the fields in and shows them.
 *
 * Looking a reg up also pulls its customer forward when the vehicle is already
 * on file, so the common case — a returning customer — is one field of typing.
 */

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Pre-seed the date (and time, if non-midnight) — e.g. clicking a calendar day. */
    initialDate?: Date;
}

interface CustomerRow {
    id: string;
    full_name: string;
    phone: string | null;
    email: string | null;
}

interface VehicleRow {
    id: string;
    vrm: string;
    make: string | null;
    model: string | null;
    colour?: string | null;
    year?: number | null;
    engine?: string | null;
    fuel_type?: string | null;
    mileage?: number | null;
    mot_last_odometer_value?: string | null;
    mot_last_odometer_unit?: string | null;
    mot_last_test_date?: string | null;
}

type MotOffer = { miles: number; testDate: string | null };

const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

type SelectedCustomer =
    | { kind: 'existing'; row: CustomerRow }
    | { kind: 'new'; name: string; phone: string; email: string };

/**
 * Everything the appointment RPC can persist about a vehicle, as strings so the
 * inputs are controlled and a half-typed number is not lost to NaN.
 */
interface VehicleForm {
    vrm: string;
    make: string;
    model: string;
    colour: string;
    year: string;
    engine: string;
    fuelType: string;
    mileage: string;
    /** ISO dates from DVLA — not edited here, but carried through to the row. */
    motExpiry: string | null;
    taxExpiry: string | null;
}

const blankVehicle = (): VehicleForm => ({
    vrm: '', make: '', model: '', colour: '', year: '',
    engine: '', fuelType: '', mileage: '', motExpiry: null, taxExpiry: null,
});

const SLOT_START_HOUR = 7;
const SLOT_END_HOUR = 21; // last slot is 20:30 — workshop close-of-day
const SLOT_DURATION_MINUTES = 30;

const todayISODate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const generateSlots = (): string[] => {
    const slots: string[] = [];
    for (let h = SLOT_START_HOUR; h < SLOT_END_HOUR; h++) {
        for (let m = 0; m < 60; m += SLOT_DURATION_MINUTES) {
            slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
    }
    return slots;
};

const isValidHHMM = (s: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(s);

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * One mapper for both shapes a confirmed vehicle arrives in.
 *
 * A DVLA hit is camelCase off the enquiry API (`yearOfManufacture`,
 * `engineCapacity`, `motExpiryDate`); a vehicle already on file is a raw
 * `vehicles` row (`year`, `engine`, `mot_expiry`). `FinalVehicleData` is the
 * union of the two, so reading only one set silently loses half the payload —
 * which is the bug this whole rewrite exists to close.
 */
const vehicleFormFromLookup = (v: FinalVehicleData): VehicleForm => {
    const row = v as unknown as Record<string, unknown>;
    return {
        vrm: str(v.vrm).toUpperCase(),
        make: str(v.make),
        model: str(v.model),
        colour: str(v.colour),
        year: str(v.year ?? v.yearOfManufacture),
        engine: str(v.engine ?? v.engineCapacity),
        fuelType: str(v.fuel_type ?? v.fuelType),
        mileage: str(v.mileage),
        motExpiry: str(v.motExpiryDate ?? row['mot_expiry']) || null,
        taxExpiry: str(v.taxDueDate ?? row['tax_expiry']) || null,
    };
};

const vehicleFormFromRow = (v: VehicleRow): VehicleForm => ({
    ...blankVehicle(),
    vrm: v.vrm,
    make: str(v.make),
    model: str(v.model),
    colour: str(v.colour),
    year: str(v.year),
    engine: str(v.engine),
    fuelType: str(v.fuel_type),
    mileage: str(v.mileage),
});

/** Integer columns reject '' and 'abc'; send NULL rather than a failed insert. */
const toIntOrNull = (s: string): number | null => {
    const n = Number.parseInt(s.replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) ? n : null;
};

export const BookingModal = ({ isOpen, onClose, onSuccess, initialDate }: Props) => {
    const { activeOrgId } = useVertical();

    // ── Vehicle ──────────────────────────────────────────────
    const [vehicle, setVehicle] = useState<VehicleForm>(blankVehicle);
    const setV = (patch: Partial<VehicleForm>) => setVehicle((prev) => ({ ...prev, ...patch }));
    const [lookupDone, setLookupDone] = useState(false);
    // The odometer at the vehicle's last MOT, OFFERED for the mileage box and
    // never written into it on its own: whatever is in that box is saved as
    // vehicles.mileage, and an MOT reading can be eleven months old
    // (20260904090000). Same rule as NewJobModal.
    const [motOffer, setMotOffer] = useState<MotOffer | null>(null);
    // The test date once the user has TAKEN the offer, so the field can say
    // where the number came from. Cleared the moment they type their own.
    const [mileageFromMotOn, setMileageFromMotOn] = useState<string | null>(null);

    // ── Customer ─────────────────────────────────────────────
    // What the customer actually said on the phone. The RPC has always taken
    // p_notes and this modal once sent null, so a booking made here carried
    // nothing but a type icon — the reason for the visit had to be retyped
    // when the job was raised.
    const [notes, setNotes] = useState('');
    const [customerQuery, setCustomerQuery] = useState('');
    const [customerResults, setCustomerResults] = useState<CustomerRow[]>([]);
    const [newCustomerName, setNewCustomerName] = useState('');
    const [newCustomerPhone, setNewCustomerPhone] = useState('');
    const [newCustomerEmail, setNewCustomerEmail] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
    const debounceRef = useRef<number | null>(null);

    const [customerVehicles, setCustomerVehicles] = useState<VehicleRow[]>([]);

    // ── Type ─────────────────────────────────────────────────
    const [appointmentType, setAppointmentType] = useState<string>('checkup');

    // ── When ─────────────────────────────────────────────────
    const [date, setDate] = useState<string>(todayISODate());
    const [time, setTime] = useState<string | null>(null);
    const [customTime, setCustomTime] = useState<string>('');
    const [takenSlots, setTakenSlots] = useState<Set<string>>(new Set());

    // ── Submit ───────────────────────────────────────────────
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const slots = useMemo(() => generateSlots(), []);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setVehicle(blankVehicle());
            setLookupDone(false);
            setMotOffer(null);
            setMileageFromMotOn(null);
            setNotes('');
            setCustomerQuery('');
            setCustomerResults([]);
            setNewCustomerName('');
            setNewCustomerPhone('');
            setNewCustomerEmail('');
            setSelectedCustomer(null);
            setCustomerVehicles([]);
            setAppointmentType('checkup');
            setDate(todayISODate());
            setTime(null);
            setCustomTime('');
            setTakenSlots(new Set());
            setSubmitting(false);
            setSubmitError(null);
        }
    }, [isOpen]);

    // Seed date/time when opened from a calendar day click.
    useEffect(() => {
        if (!isOpen || !initialDate) return;
        const d = initialDate;
        const pad = (n: number) => String(n).padStart(2, '0');
        setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
        if (d.getHours() !== 0 || d.getMinutes() !== 0) {
            const hhmm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
            // Snaps to a slot when it lands on :00/:30, otherwise a custom time.
            if (d.getMinutes() % SLOT_DURATION_MINUTES === 0 && d.getHours() >= SLOT_START_HOUR && d.getHours() < SLOT_END_HOUR) {
                setTime(hhmm);
                setCustomTime('');
            } else {
                setCustomTime(hhmm);
                setTime(null);
            }
        }
    }, [isOpen, initialDate]);

    // Debounced customer search
    useEffect(() => {
        if (!activeOrgId) return;
        if (selectedCustomer) return; // freeze search once selected
        if (debounceRef.current) window.clearTimeout(debounceRef.current);

        const q = customerQuery.trim();
        if (q.length < 2) {
            setCustomerResults([]);
            return;
        }

        debounceRef.current = window.setTimeout(async () => {
            // Archived customers are hidden from search by design (20260904120000).
            const { data, error: fetchError } = await supabase
                .from('customers_decrypted')
                .select('id, full_name, phone, email')
                .eq('org_id', activeOrgId)
                .is('archived_at', null)
                .ilike('full_name', `%${q}%`)
                .limit(8);
            if (fetchError) reportError(fetchError, 'booking-modal.customers-decrypted');
            setCustomerResults((data as CustomerRow[]) ?? []);
        }, 250);

        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, [customerQuery, activeOrgId, selectedCustomer]);

    // When an existing customer is picked, offer their vehicles — but only as a
    // shortcut. The reg has usually been entered above already, in which case
    // this list stays out of the way.
    useEffect(() => {
        if (!activeOrgId || !selectedCustomer || selectedCustomer.kind !== 'existing') {
            setCustomerVehicles([]);
            return;
        }
        supabase
            .from('vehicles')
            .select('id, vrm, make, model, colour, year, engine, fuel_type, mileage, mot_last_odometer_value, mot_last_odometer_unit, mot_last_test_date')
            .eq('org_id', activeOrgId)
            .eq('owner_id', selectedCustomer.row.id)
            .order('created_at', { ascending: false })
            .then(({ data, error: vErr }) => {
                if (vErr) reportError(vErr, 'booking-modal.vehicles');
                setCustomerVehicles((data as VehicleRow[]) ?? []);
            });
    }, [activeOrgId, selectedCustomer]);

    // Fetch taken slots for the selected date
    useEffect(() => {
        if (!activeOrgId || !date) return;
        const [yyyy, mm, dd] = date.split('-').map(Number);
        const start = new Date(yyyy, mm - 1, dd, 0, 0, 0).toISOString();
        const end = new Date(yyyy, mm - 1, dd, 23, 59, 59).toISOString();

        supabase
            .rpc('fn_get_appointments_range', {
                p_org_id: activeOrgId,
                p_start_date: start,
                p_end_date: end,
            })
            .then(({ data, error: rErr }) => {
                if (rErr) reportError(rErr, 'booking-modal.fn_get_appointments_range');
                if (!Array.isArray(data)) return;
                const taken = new Set<string>();
                for (const appt of data as Array<{ start_time: string; status: string }>) {
                    if (appt.status === 'cancelled' || appt.status === 'no_show') continue;
                    const d = new Date(appt.start_time);
                    const key = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                    taken.add(key);
                }
                setTakenSlots(taken);
                // Functional update + `time` out of the deps: including it made
                // the effect refetch on every slot the user clicked, and clearing
                // it from inside its own effect risked a loop.
                setTime((prev) => (prev && taken.has(prev) ? null : prev));
            });
    }, [activeOrgId, date]);

    if (!isOpen) return null;

    // A booking needs a reg and a name. Everything else is detail the lookup
    // fills in — and which is now saved rather than discarded.
    const customerName =
        selectedCustomer?.kind === 'existing'
            ? selectedCustomer.row.full_name
            : selectedCustomer?.kind === 'new'
              ? selectedCustomer.name
              : newCustomerName.trim();
    const customerReady = customerName.length > 0;
    const vehicleReady = vehicle.vrm.trim().length > 0;
    const finalTime = time && isValidHHMM(time) ? time : (customTime && isValidHHMM(customTime) ? customTime : null);
    const whenReady = date.length > 0 && finalTime !== null;
    const allReady = customerReady && vehicleReady && whenReady;

    const applyLookup = (v: FinalVehicleData) => {
        setVehicle(vehicleFormFromLookup(v));
        setMotOffer(motOdometerOffer(v));
        setMileageFromMotOn(null);
        setLookupDone(true);
    };

    const applyRow = (v: VehicleRow) => {
        setVehicle(vehicleFormFromRow(v));
        setMotOffer(motOdometerOffer(v));
        setMileageFromMotOn(null);
        setLookupDone(true);
    };

    // The reg is on file, so we know whose car it is — pull the owner forward
    // rather than making the user search for a customer they just identified.
    const applyLinkedCustomer = (c: LinkedCustomerData) => {
        if (selectedCustomer) return;
        setSelectedCustomer({ kind: 'existing', row: { id: c.id, full_name: c.full_name, phone: c.phone, email: c.email } });
    };

    const clearCustomer = () => {
        setSelectedCustomer(null);
        setNewCustomerName('');
        setNewCustomerPhone('');
        setNewCustomerEmail('');
        setCustomerQuery('');
        setCustomerVehicles([]);
    };

    const submit = async () => {
        if (!activeOrgId || !customerReady || !vehicleReady || !finalTime) return;
        setSubmitting(true);
        setSubmitError(null);

        const [yyyy, mm, dd] = date.split('-').map(Number);
        const [hh, min] = finalTime.split(':').map(Number);
        const startLocal = new Date(yyyy, mm - 1, dd, hh, min, 0);
        const endLocal = new Date(startLocal.getTime() + SLOT_DURATION_MINUTES * 60_000);

        const customerPhone =
            selectedCustomer?.kind === 'existing'
                ? (selectedCustomer.row.phone ?? '')
                : selectedCustomer?.kind === 'new'
                  ? selectedCustomer.phone
                  : newCustomerPhone.trim();
        const customerEmail =
            selectedCustomer?.kind === 'existing'
                ? (selectedCustomer.row.email ?? '')
                : selectedCustomer?.kind === 'new'
                  ? selectedCustomer.email
                  : newCustomerEmail.trim();

        const { data: created, error } = await supabase.rpc('fn_create_appointment_with_details', {
            p_org_id: activeOrgId,
            p_customer_name: customerName,
            p_customer_phone: customerPhone,
            p_customer_email: customerEmail,
            p_vehicle_vrm: vehicle.vrm.trim().toUpperCase(),
            p_vehicle_make: vehicle.make.trim() || null,
            p_vehicle_model: vehicle.model.trim() || null,
            p_start_time: startLocal.toISOString(),
            p_end_time: endLocal.toISOString(),
            p_notes: notes.trim() || null,
            // Everything below this line used to be fetched from the DVLA,
            // shown to the user, and then thrown away.
            p_vehicle_colour: vehicle.colour.trim() || null,
            p_vehicle_year: toIntOrNull(vehicle.year),
            p_vehicle_engine: vehicle.engine.trim() || null,
            p_vehicle_fuel_type: vehicle.fuelType.trim() || null,
            p_vehicle_mileage: toIntOrNull(vehicle.mileage),
            p_vehicle_mot_expiry: vehicle.motExpiry,
            p_vehicle_tax_expiry: vehicle.taxExpiry,
            p_appointment_type: appointmentType,
            // A customer picked from the list is booked as that customer. Without
            // the id the RPC re-matched by phone/email, and a customer with none
            // (or none readable) became a duplicate.
            p_customer_id: selectedCustomer?.kind === 'existing' ? selectedCustomer.row.id : null,
        });

        if (error) {
            reportError(error, 'booking-modal.fn_create_appointment_with_details');
            setSubmitting(false);
            setSubmitError(error.message || 'Could not create booking. Please try again.');
            return;
        }
        void created;

        setSubmitting(false);
        onSuccess();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="New Appointment" subtitle="Book a visit for your customer" editor
            footer={<div>                    <button
                        onClick={submit}
                        disabled={!allReady || submitting}
                        className="fd-btn fd-btn-go fd-btn-lg w-full"
                    >
                        {submitting ? 'Booking…' : 'Confirm Booking'}
                    </button>
                    {!allReady && (
                        <p className="text-center sm:text-right text-xs text-fg-3 mt-2">
                            {!vehicleReady
                                ? 'Add a registration to continue.'
                                : !customerReady
                                  ? 'Add a customer name to continue.'
                                  : 'Pick a time to continue.'}
                        </p>
                    )}</div>}
        >
                {/* The reg first and full width (it can fill the customer too),
                    then who + what side by side, then the time slots full width
                    so the grid of times has room to breathe. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">

                    {/* ── Vehicle ────────────────────────────────────
                        First, because the reg is the fastest way in: it fills
                        the vehicle AND, when the car is already on file, the
                        customer too. */}
                    <Section icon={<Car className="w-4 h-4" />} title="What car?" done={vehicleReady} className="md:col-span-2">
                        <VRMLookup
                            onVehicleConfirmed={applyLookup}
                            onVehicleCleared={() => {
                                setVehicle(blankVehicle());
                                setLookupDone(false);
                                setMotOffer(null);
                                setMileageFromMotOn(null);
                                setSubmitError(null);
                            }}
                            onCustomerFound={applyLinkedCustomer}
                            onSkipToManual={(vrm) => {
                                // A fresh vehicle, not the previous one with a new
                                // reg: otherwise its make, model and an accepted MOT
                                // mileage would be saved against this car.
                                setVehicle({ ...blankVehicle(), vrm: (vrm || '').toUpperCase() });
                                setMotOffer(null);
                                setMileageFromMotOn(null);
                                setLookupDone(true);
                            }}
                        />

                        {!lookupDone && (
                            <button
                                onClick={() => setLookupDone(true)}
                                className="mt-2 text-xs text-gold hover:underline"
                            >
                                Or enter the vehicle by hand
                            </button>
                        )}

                        {/* Always visible once there is something to show. The
                            fields are editable: the DVLA does not know the
                            mileage, and a trade plate or an import may need a
                            correction before it is saved. */}
                        {(lookupDone || vehicleReady) && (
                            <div className="mt-3 space-y-2">
                                <Field label="Registration">
                                    <input
                                        type="text"
                                        value={vehicle.vrm}
                                        onChange={(e) => setV({ vrm: e.target.value.toUpperCase() })}
                                        className={`${inputCls} font-mono uppercase tracking-widest`}
                                    />
                                </Field>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <Field label="Make">
                                        <input type="text" value={vehicle.make} onChange={(e) => setV({ make: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Model">
                                        <input type="text" value={vehicle.model} onChange={(e) => setV({ model: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Year">
                                        <input type="text" inputMode="numeric" value={vehicle.year} onChange={(e) => setV({ year: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Colour">
                                        <input type="text" value={vehicle.colour} onChange={(e) => setV({ colour: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Engine (cc)">
                                        <input type="text" inputMode="numeric" value={vehicle.engine} onChange={(e) => setV({ engine: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Fuel">
                                        <input type="text" value={vehicle.fuelType} onChange={(e) => setV({ fuelType: e.target.value })} className={inputCls} />
                                    </Field>
                                </div>
                                <Field label="Mileage">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={vehicle.mileage}
                                        onChange={(e) => {
                                            setV({ mileage: e.target.value });
                                            setMileageFromMotOn(null);
                                        }}
                                        className={inputCls}
                                    />
                                </Field>
                                {mileageFromMotOn !== null ? (
                                    <p className="text-[11px] text-gold">
                                        From the last MOT{mileageFromMotOn ? ` (${fmtDate(mileageFromMotOn)})` : ''} — overwrite it with today's if the car has moved since.
                                    </p>
                                ) : !vehicle.mileage && motOffer ? (
                                    // One tap to accept, never filled in for them:
                                    // this box is saved as the vehicle's mileage.
                                    <MotMileageOffer
                                        miles={motOffer.miles}
                                        testDate={motOffer.testDate}
                                        onUse={() => {
                                            setV({ mileage: String(motOffer.miles) });
                                            setMileageFromMotOn(motOffer.testDate ?? '');
                                        }}
                                    />
                                ) : !vehicle.mileage && (
                                    <p className="text-[11px] text-fg-4">No reading on file yet — add today's.</p>
                                )}
                            </div>
                        )}
                    </Section>

                    {/* ── Customer ──────────────────────────────────── */}
                    <Section icon={<User className="w-4 h-4" />} title="Who's coming in?" done={customerReady}>
                        {selectedCustomer ? (
                            <SelectedChip
                                primary={customerName}
                                secondary={
                                    selectedCustomer.kind === 'existing'
                                        ? (selectedCustomer.row.phone ?? '')
                                        : selectedCustomer.phone
                                }
                                onChange={clearCustomer}
                            />
                        ) : (
                            <div className="space-y-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-4" />
                                    <input
                                        type="text"
                                        placeholder="Search customers already on file…"
                                        value={customerQuery}
                                        onChange={(e) => setCustomerQuery(e.target.value)}
                                        className={`${inputCls} pl-9`}
                                    />
                                </div>
                                {customerResults.length > 0 && (
                                    <ul className="border border-line rounded-md overflow-hidden divide-y divide-line">
                                        {customerResults.map((c) => (
                                            <li key={c.id}>
                                                <button
                                                    onClick={() => setSelectedCustomer({ kind: 'existing', row: c })}
                                                    className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-pressed transition-colors flex items-center justify-between gap-2"
                                                >
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="text-sm text-fg truncate">{c.full_name}</span>
                                                        {c.phone && (
                                                            <span className="text-[11px] font-mono text-fg-3 truncate">{c.phone}</span>
                                                        )}
                                                    </div>
                                                    <ChevronRight className="w-4 h-4 text-fg-4 shrink-0" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* No mode switch, no "Add new customer" button
                                    to find: typing a name here IS adding one.
                                    The old flow made you choose between search
                                    and create before you knew which you needed. */}
                                <div className="rounded-lg border border-line bg-raised p-3 sm:p-4 space-y-2">
                                    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold">
                                        <UserPlus className="w-3.5 h-3.5" /> Or a new customer
                                    </p>
                                    <Field label="Name">
                                        <input type="text" value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} className={inputCls} />
                                    </Field>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <Field label="Phone">
                                            <input type="tel" value={newCustomerPhone} onChange={(e) => setNewCustomerPhone(e.target.value)} className={inputCls} />
                                        </Field>
                                        {/* Captured at the point of booking. A customer created
                                            here with no email cannot later be sent a quote or an
                                            invoice without someone going back to ask for it — by
                                            which time they have driven away. */}
                                        <Field label="Email">
                                            <input type="email" autoComplete="email" value={newCustomerEmail} onChange={(e) => setNewCustomerEmail(e.target.value)} className={inputCls} />
                                        </Field>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* A shortcut, not a step: only worth showing when the
                            customer has more than one car and no reg is set. */}
                        {selectedCustomer && !vehicleReady && customerVehicles.length > 0 && (
                            <div className="mt-3 space-y-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold">
                                    Vehicles on file
                                </p>
                                <ul className="border border-line rounded-md overflow-hidden divide-y divide-line">
                                    {customerVehicles.map((v) => (
                                        <li key={v.id}>
                                            <button
                                                onClick={() => applyRow(v)}
                                                className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-pressed transition-colors flex items-center justify-between gap-2"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-[11px] font-bold tracking-wider shrink-0"
                                                        style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                                                    >
                                                        {v.vrm}
                                                    </span>
                                                    <span className="text-xs text-fg-3 truncate">
                                                        {[v.make, v.model].filter(Boolean).join(' ')}
                                                    </span>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-fg-4 shrink-0" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Section>

                    {/* ── What for? ──────────────────────────────────── */}
                    <Section icon={<CalendarIcon className="w-4 h-4" />} title="What's it for?" done={!!appointmentType}>
                        <div className="grid grid-cols-3 gap-2">
                            {APPOINTMENT_TYPES.map((t) => {
                                const Icon = t.icon;
                                const selected = appointmentType === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        onClick={() => setAppointmentType(t.value)}
                                        aria-pressed={selected}
                                        className={`h-16 rounded-md transition-colors flex flex-col items-center justify-center gap-1 min-w-0 px-1 border ${
                                            selected
                                                ? 'bg-gold-fill/10 border-gold/50 text-gold shadow-[0_0_0_1px_var(--fd-gold-line)]'
                                                : 'bg-raised border-line-strong text-fg-2 hover:bg-pressed hover:text-fg'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4 shrink-0" strokeWidth={selected ? 2.25 : 1.75} />
                                        <span className="text-[11px] font-medium truncate max-w-full">{t.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-3">
                            <Field label="What did they say?" hint="Knocking on left turn, service light on…">
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className={`${inputCls} h-auto py-2 resize-none`}
                                />
                            </Field>
                        </div>
                    </Section>

                    {/* ── When ───────────────────────────────────────── */}
                    <Section icon={<CalendarIcon className="w-4 h-4" />} title="When?" done={whenReady} className="md:col-span-2">
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Date">
                                    <input
                                        type="date"
                                        value={date}
                                        min={todayISODate()}
                                        onChange={(e) => setDate(e.target.value)}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Or a custom time">
                                    <input
                                        type="time"
                                        value={customTime}
                                        onChange={(e) => {
                                            setCustomTime(e.target.value);
                                            setTime(null);
                                        }}
                                        step={300}
                                        className={`${inputCls} font-mono`}
                                    />
                                </Field>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold mb-2">
                                    Available slots — 07:00 to 21:00
                                </p>
                                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                                    {slots.map((slot) => {
                                        const taken = takenSlots.has(slot);
                                        const selected = (time ?? customTime) === slot;
                                        return (
                                            <button
                                                key={slot}
                                                disabled={taken}
                                                onClick={() => {
                                                    setTime(slot);
                                                    setCustomTime('');
                                                }}
                                                aria-pressed={selected}
                                                className={`h-10 rounded-md font-mono text-sm tabular-nums transition-colors border ${
                                                    taken
                                                        ? 'text-fg-4 bg-sunken border-transparent line-through cursor-not-allowed'
                                                        : selected
                                                          ? 'fd-btn-primary font-semibold'
                                                          : 'bg-raised text-fg-2 border-line-strong hover:bg-pressed hover:text-fg'
                                                }`}
                                            >
                                                {slot}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* ── Confirm ────────────────────────────────────── */}
                    {allReady && finalTime && (
                        <div className="md:col-span-2 fd-card fd-card-quiet p-5 space-y-1.5">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold mb-1.5">
                                Summary
                            </p>
                            <SummaryRow label="Customer" value={customerName} />
                            <SummaryRow
                                label="Vehicle"
                                value={[vehicle.vrm, [vehicle.make, vehicle.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                            />
                            <SummaryRow label="Type" value={APPOINTMENT_TYPES.find((t) => t.value === appointmentType)?.label ?? appointmentType} />
                            <SummaryRow label="Date" value={new Date(`${date}T00:00:00`).toLocaleDateString('en-GB')} />
                            <SummaryRow label="Time" value={finalTime} />
                        </div>
                    )}

                    {submitError && (
                        <div className="md:col-span-2 rounded-md bg-danger/10 border border-danger/30 px-3 py-2 text-xs text-danger">
                            {submitError}
                        </div>
                    )}


                </div>
        </Modal>
    );
};

/* ── Sub-components ──────────────────────────────────────── */

const inputCls =
    'w-full h-10 px-3 bg-canvas border border-line rounded-md text-sm text-fg outline-none transition-all placeholder:text-fg-4 focus:border-gold focus:shadow-focus';

/**
 * Every input in this modal carries a visible label rather than a placeholder.
 * A placeholder disappears the moment you type into it, which is exactly when
 * you most want to know what the box was for.
 */
const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <label className="block min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.16em] text-fg-3 font-display font-semibold">
            {label}
        </span>
        {hint && <span className="block text-[11px] text-fg-4 mt-0.5">{hint}</span>}
        <span className="block mt-1">{children}</span>
    </label>
);

/* Each step is a real card (fd-card) — the same surface as the dashboard —
   so the booking form reads as part of the app, not a grey sheet. */
const Section = ({
    icon,
    title,
    done,
    className = '',
    children,
}: {
    icon: React.ReactNode;
    title: string;
    done: boolean;
    className?: string;
    children: React.ReactNode;
}) => (
    <section className={`fd-card min-w-0 p-4 sm:p-5 ${className}`}>
        <header className="flex items-center gap-2.5 mb-4">
            <span className={`fd-step-dot ${done ? 'is-done' : ''}`} aria-label={done ? 'Done' : 'To do'}>
                {done ? <Check className="w-3 h-3" strokeWidth={3} /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
            </span>
            <span className="text-fg-3 shrink-0">{icon}</span>
            <h3 className="text-base font-display font-semibold text-fg truncate">{title}</h3>
        </header>
        {children}
    </section>
);

const SelectedChip = ({
    primary,
    secondary,
    onChange,
}: {
    primary: string;
    secondary?: string;
    onChange: () => void;
}) => (
    <div className="flex items-center justify-between gap-3 rounded-md bg-gold-fill/10 border border-gold/25 px-3 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-sm font-medium text-fg truncate">{primary}</span>
            {secondary && <span className="text-xs text-fg-3 truncate">{secondary}</span>}
        </div>
        <button onClick={onChange} className="text-[11px] text-gold hover:underline shrink-0">
            Change
        </button>
    </div>
);

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start justify-between gap-3 text-xs">
        <span className="text-fg-3 uppercase tracking-wider shrink-0">{label}</span>
        <span className="font-mono text-fg tabular-nums text-right break-words min-w-0">{value}</span>
    </div>
);
